import { Component, EventEmitter, OnDestroy, OnInit, inject } from '@angular/core';
import { Subject, filter, takeUntil } from 'rxjs';
import { CustomerFormComponent } from './customer-form.component';
import { BillingTourCoordinatorService } from '../billing-wizard/billing-tour-coordinator.service';
import { BillingOnboardingTourService } from './billing-onboarding-tour.service';
import {
  Customer,
  ServiceAndLocation,
  OnBoardingRecord,
  LocationProducts,
  StepInfo,
} from '@voice/utils';
import { ButtonModule } from 'primeng/button';
import { ClientOnboardingService } from '../../../services/client-onboarding.service';
import { NotificationsService } from '../../../services/notifications.service';
import { ActivatedRoute, Router } from '@angular/router';
import { NgClass, NgIf } from '@angular/common';
import { StepperModule } from 'primeng/stepper';
import { LocationsListComponent } from './locations-list.component';
import {
  allLocationsAreValid,
  allProductsAreValid,
  allServicesAreValid,
  Step,
  StepKey,
} from './onboarding.utils';
import { ServicesListComponent } from './services-list.component';
import { ProductsListComponent } from './products-list.component';
import { SummaryComponent } from './summary.component';

@Component({
  selector: 'app-client-onboarding',
  standalone: true,
  templateUrl: './onboarding-wizard.component.html',
  imports: [
    CustomerFormComponent,
    ButtonModule,
    NgIf,
    StepperModule,
    NgClass,
    LocationsListComponent,
    ServicesListComponent,
    ProductsListComponent,
    SummaryComponent,
  ],
})
export class OnboardingWizardComponent implements OnInit, OnDestroy {
  private readonly tourCoordinator = inject(BillingTourCoordinatorService);
  private readonly tourService = inject(BillingOnboardingTourService);
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly clientOnboardingService: ClientOnboardingService,
    private readonly notificationService: NotificationsService
  ) {}

  onBoardingRecordId?: number;
  onBoardingRecord?: OnBoardingRecord;
  customer!: Customer;
  locations!: ServiceAndLocation[];
  items!: LocationProducts[];
  steps: Step[] = [];
  active = 0;
  isProcessingRevio = false;

  get parentCustomerId(): number | undefined {
    return (this.onBoardingRecord as any)?.parentCustomerId;
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe({
      next: (params) => {
        if (params['id']) {
          this.initWizardSteps(false);
          this.onBoardingRecordId = parseInt(params['id']);
          this.getData().then(() => {
            this.verifyStepsValidity(
              this.onBoardingRecord?.steps as StepInfo[]
            );
          });
        } else {
          this.customer = {} as Customer;
          this.locations = [];
          this.initWizardSteps(true);
        }
      },
      error: (err) => this.notificationService.error(err),
    });

    this.tourCoordinator.tourRequest$.pipe(
      filter(id => id === 'onboarding'),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      setTimeout(() => this.tourService.start({}), 150);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initWizardSteps(disabled: boolean): void {
    this.steps.push({
      key: StepKey.CUSTOMER,
      title: 'Customer',
      description: 'Enter customer information',
      icon: 'pi pi-user',
      disabled: false,
      valid: false,
    });
    this.steps.push({
      key: StepKey.LOCATIONS,
      title: 'Locations',
      description: 'Add service locations',
      icon: 'pi pi-map-marker',
      disabled: disabled,
      valid: false,
    });
    this.steps.push({
      key: StepKey.SERVICES,
      title: 'Services',
      description: 'Configure services',
      icon: 'pi pi-cog',
      disabled: disabled,
      valid: false,
    });
    this.steps.push({
      key: StepKey.ITEMS,
      title: 'Items',
      description: 'Select products',
      icon: 'pi pi-list',
      disabled: disabled,
      valid: false,
    });
    this.steps.push({
      key: StepKey.SUMMARY,
      title: 'Summary',
      description: 'Review details',
      icon: 'pi pi-check-circle',
      disabled: disabled,
      valid: false,
    });
  }

  customerSavedHandler(
    event: OnBoardingRecord,
    step: Step,
    nextCallback: EventEmitter<void>
  ) {
    step.valid = true;
    for (const step of this.steps) {
      step.disabled = false;
    }

    if (event) {
      this.onBoardingRecordId = event.id;
      this.getData().then(() => {
        if (this.onBoardingRecord?.steps) {
          this.verifyStepsValidity(this.onBoardingRecord?.steps as StepInfo[]);
          nextCallback.emit();
        } else {
          const stepsInfo: StepInfo[] = this.createStepsInfo();
          this.clientOnboardingService
            .saveSteps(this.onBoardingRecordId || event.id, stepsInfo)
            .subscribe({
              next: () => {
                // Reload data to get updated steps
                this.getData().then(() => {
                  if (this.onBoardingRecord?.steps) {
                    this.verifyStepsValidity(
                      this.onBoardingRecord.steps as StepInfo[]
                    );
                  } else {
                    this.verifyStepsValidity(stepsInfo);
                  }
                  nextCallback.emit();
                });
              },
              error: (err) => this.notificationService.error(err),
            });
        }
      });
    } else {
      nextCallback.emit();
    }
  }

  navigationHandler(
    event: string,
    prevCallback: EventEmitter<void>,
    nextCallback: EventEmitter<void>
  ): void {
    this.getData().then(() => {
      const stepsInfo = this.createStepsInfo();
      this.verifyStepsValidity(stepsInfo);
      if (this.onBoardingRecordId) {
        this.clientOnboardingService
          .saveSteps(this.onBoardingRecordId, stepsInfo)
          .subscribe({
            next: () => {
              // Reload data to get updated steps
              this.getData().then(() => {
                if (this.onBoardingRecord?.steps) {
                  this.verifyStepsValidity(
                    this.onBoardingRecord.steps as StepInfo[]
                  );
                }
                if (event === 'next') {
                  nextCallback.emit();
                } else {
                  prevCallback.emit();
                }
              });
            },
            error: (err) => this.notificationService.error(err),
          });
      } else {
        if (event === 'next') {
          nextCallback.emit();
        } else {
          prevCallback.emit();
        }
      }
    });
  }

  goToRecords() {
    this.router.navigate(['/billing/client-onboarding']).then();
  }

  onProcessingStateChange(isProcessing: boolean): void {
    this.isProcessingRevio = isProcessing;
    if (isProcessing) {
      this.steps.forEach((step, index) => {
        if (index !== this.active) {
          step.disabled = true;
        }
      });
    } else {
      this.steps.forEach((step) => {
        if (step.key === StepKey.CUSTOMER) {
          step.disabled = false;
        } else {
          step.disabled = !this.onBoardingRecord?.id;
        }
      });
    }
  }

  private createStepsInfo(): StepInfo[] {
    const locationsValid = allLocationsAreValid(
      this.onBoardingRecord?.locations as ServiceAndLocation[]
    );
    const servicesValid = allServicesAreValid(
      this.onBoardingRecord?.locations as ServiceAndLocation[]
    );

    const savedProducts = this.onBoardingRecord?.products as LocationProducts[];
    const productsValid = allProductsAreValid(savedProducts);

    const summaryValid = locationsValid && servicesValid && productsValid;

    return [
      {
        step: StepKey.CUSTOMER,
        valid: true,
      },
      {
        step: StepKey.LOCATIONS,
        valid: locationsValid,
      },
      {
        step: StepKey.SERVICES,
        valid: servicesValid,
      },
      {
        step: StepKey.ITEMS,
        valid: productsValid,
      },
      {
        step: StepKey.SUMMARY,
        valid: summaryValid,
      },
    ];
  }

  private verifyStepsValidity(stepsInfo?: StepInfo[]) {
    if (stepsInfo && Array.isArray(stepsInfo)) {
      this.steps.forEach((step) => {
        const stepInfo = stepsInfo.find((s) => s.step === step.key);
        if (stepInfo) {
          step.valid = stepInfo.valid === true;
        } else {
          step.valid = false;
        }
      });
    } else {
      // If no stepsInfo, set all to false except customer
      this.steps.forEach((step) => {
        step.valid = step.key === StepKey.CUSTOMER;
      });
    }
  }

  private getData(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.onBoardingRecordId) {
        this.clientOnboardingService.find(this.onBoardingRecordId).subscribe({
          next: (data) => {
            this.onBoardingRecord = data;
            this.customer = data.customer as Customer;
            this.locations = data.locations as ServiceAndLocation[];
            this.items = data.products as LocationProducts[];
            resolve();
          },
          error: (err) => reject(err),
        });
      }
    });
  }
}
